import { useEffect, useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import {
  useInstagramPlatformCredential,
  useUpsertInstagramPlatformCredential,
} from '../hooks/useInstagram';

/**
 * TCS-admin platform Instagram app credential (PE-2178 tier 3, backend PE-2177).
 *
 * ONE shared Meta app for every merchant OAuth flow. The secret is encrypted at rest by
 * watchman and never returned by any read — the form therefore shows "on file" rather than a
 * masked value, and saving always requires re-entering it.
 *
 * ⚠️ THIS PAGE 403s FOR EVERY ROLE TODAY. Both routes require MERCHANT.MANAGE; watchman's
 * seed grants COLLECTIVE_SUPERADMIN MERCHANT:[CREATE, READ, UPDATE, DEACTIVATE, DELETE] and
 * PermissionGuard is a literal `chars.includes('M')` test with no superadmin bypass. That is
 * filed as a backend ask (grant MERCHANT.MANAGE, or add a per-user PERMISSION.GRANT
 * override). Until it lands the whole Instagram connect flow is blocked behind it, because
 * `buildAuthorizeUrl` throws "Instagram platform app credential is not configured" with no
 * credential on file. A 403 is therefore rendered as an explicit, named permission state —
 * not as a generic "something went wrong".
 */
export const InstagramPlatformCredentialPage = () => {
  const { hasPermission } = usePermissions();
  // MERCHANT_MANAGE mirrors watchman's MERCHANT.MANAGE, which nothing grants yet — so this is
  // false for everyone and the page renders the blocked state below rather than a dead form.
  const canManage = hasPermission(Permission.MERCHANT_MANAGE);

  const credentialQuery = useInstagramPlatformCredential(canManage);
  const upsert = useUpsertInstagramPlatformCredential();

  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');

  const credential = credentialQuery.data;

  useEffect(() => {
    if (!credential) return;
    setAppId(credential.appId);
    setRedirectUri(credential.redirectUri);
  }, [credential]);

  const forbidden = (credentialQuery.error as { status?: number } | null)?.status === 403;

  const Heading = (
    <div className="mb-5">
      <h1 className="text-[22px] font-semibold text-foreground">Instagram App Credential</h1>
      <p className="text-[13px] text-muted-foreground mt-1">
        The single Meta app every merchant's Instagram connection authorises against. Rotating
        it re-points all future connections; existing tokens keep working until they expire.
      </p>
    </div>
  );

  if (!canManage || forbidden) {
    return (
      <div className="py-6 max-w-4xl mx-auto px-2">
        {Heading}
        <div className="bg-card rounded-2xl border border-border p-6 space-y-3">
          <h2 className="text-[16px] font-semibold text-foreground">
            You do not have permission to manage this
          </h2>
          <p className="text-[14px] text-muted-foreground">
            Reading or rotating the shared Instagram app credential needs the{' '}
            <span className="text-foreground font-medium">MERCHANT.MANAGE</span> permission. No
            role currently grants it — not even TCS superadmin — so this page is read-blocked
            for everyone until the grant is added in Watchman.
          </p>
          <p className="text-[14px] text-muted-foreground">
            Until a credential is on file, merchants cannot connect Instagram at all: the
            authorize step fails with “Instagram platform app credential is not configured”.
            Merchant connection status and the oversight list are unaffected.
          </p>
        </div>
      </div>
    );
  }

  const canSubmit =
    appId.trim().length > 0 && appSecret.trim().length > 0 && redirectUri.trim().length > 0;

  const save = () => {
    if (!canSubmit) return;
    upsert.mutate(
      { appId: appId.trim(), appSecret: appSecret.trim(), redirectUri: redirectUri.trim() },
      { onSuccess: () => setAppSecret('') },
    );
  };

  return (
    <div className="py-6 max-w-4xl mx-auto px-2 space-y-5">
      {Heading}

      <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
        {credentialQuery.isLoading ? (
          <p className="text-[14px] text-muted-foreground">Loading the app credential…</p>
        ) : (
          <>
            <p className="text-[13px] text-muted-foreground">
              {credential
                ? 'A credential is on file. The secret is encrypted at rest and never returned — re-enter it to save any change.'
                : 'No credential configured yet. Merchants cannot connect Instagram until one is saved.'}
            </p>

            <Input
              label="App ID"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="1234567890"
            />
            <Input
              label={credential?.hasSecret ? 'App secret (on file — re-enter to change)' : 'App secret'}
              type="password"
              autoComplete="off"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder="••••••••••••••••"
            />
            <Input
              label="Redirect URI"
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              placeholder="https://admin.thecollectivesynergy.com/instagram/callback"
            />
            <p className="text-[12px] text-muted-foreground">
              The redirect URI must match the one registered with Meta exactly, and must point at
              this deployment's <span className="text-foreground">/instagram/callback</span>{' '}
              route — the same value is replayed during the token exchange, which fails if it
              differs.
            </p>

            <div className="flex justify-end">
              <Button
                size="sm"
                className="h-[40px]"
                onClick={save}
                disabled={!canSubmit || upsert.isPending}
                loading={upsert.isPending}
              >
                Save credential
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
